export const OPERATIONS = ["ADD", "SUBTRACT"] as const;
export type Operation = (typeof OPERATIONS)[number];

export const PROBLEM_CLASSES = [
  "ZERO_RELATION",
  "WITHIN_10",
  "TEEN_NO_BRIDGE",
  "CROSS_10_ADD",
  "CROSS_10_SUB",
  "OTHER_WITHIN_20",
] as const;
export type ProblemClassId = (typeof PROBLEM_CLASSES)[number];

export const RESPONSE_MODES = [
  "MANIPULATE",
  "GUIDED_CHOICE",
  "NUMERIC_ENTRY",
  "DIRECT_RETRIEVAL",
] as const;
export type ResponseMode = (typeof RESPONSE_MODES)[number];

export const SUPPORT_LEVELS = ["FULL", "PARTIAL", "AVAILABLE", "NONE"] as const;
export type SupportLevel = (typeof SUPPORT_LEVELS)[number];

export const LESSON_PHASES = [
  "EXPLANATION",
  "WORKED_EXAMPLE",
  "GUIDED_APPLICATION",
  "PARTIAL_APPLICATION",
  "INDEPENDENT_CHECK",
] as const;
export type LessonPhase = (typeof LESSON_PHASES)[number];

export const VISUAL_MODELS = [
  "TEN_FRAME",
  "NUMBER_BONDS",
  "PART_WHOLE",
  "NUMBER_LINE",
  "GROUPS",
  "DOUBLES_VISUAL",
] as const;
export type VisualModel = (typeof VISUAL_MODELS)[number];

export type FactFamilyId = string;
export type FactId = string;
export type SkillId = string;
export type StrategyId = string;

export interface Fact {
  readonly id: FactId;
  readonly familyId: FactFamilyId | null;
  readonly operation: Operation;
  readonly left: number;
  readonly right: number;
  readonly answer: number;
  readonly numberRange: 20;
  readonly isZeroRelation: boolean;
  readonly problemClassIds: readonly ProblemClassId[];
  readonly skillIds: readonly SkillId[];
  readonly strategyTags: readonly StrategyId[];
}

export interface FactFamily {
  readonly id: FactFamilyId;
  readonly kind: "POSITIVE";
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly factIds: readonly FactId[];
}

export interface FactCatalog {
  readonly version: string;
  readonly families: readonly FactFamily[];
  readonly facts: readonly Fact[];
  readonly familyById: ReadonlyMap<FactFamilyId, FactFamily>;
  readonly factById: ReadonlyMap<FactId, Fact>;
}

export type SkillKind =
  | "FOUNDATION"
  | "ANCHOR"
  | "RELATION"
  | "TARGET"
  | "MASTERY"
  | "TRANSFER";

export interface SkillDefinition {
  readonly id: SkillId;
  readonly kind: SkillKind;
  readonly numberRange: 20;
  readonly label: string;
  readonly description: string;
  readonly prerequisiteIds: readonly SkillId[];
}

export interface LessonStepDefinition {
  readonly phase: LessonPhase;
  readonly title: string;
  readonly copy: readonly string[];
  readonly generatorId: string;
  readonly responseMode: ResponseMode | null;
  readonly supportLevel: SupportLevel | null;
  readonly retrievalEvidence: false;
  readonly strategyEvidence: boolean;
}

export interface LessonDefinition {
  readonly id: string;
  readonly title: string;
  readonly steps: readonly LessonStepDefinition[];
  readonly exampleFactIds: readonly FactId[];
}

export interface StrategyDefinition {
  readonly id: StrategyId;
  readonly label: string;
  readonly summary: string;
  readonly supportedOperations: readonly Operation[];
  readonly appliesToSkillIds: readonly SkillId[];
  readonly prerequisiteSkillIds: readonly SkillId[];
  readonly visualModels: readonly VisualModel[];
  readonly lesson: LessonDefinition | null;
}

export interface ValidationIssue {
  readonly code: string;
  readonly message: string;
  readonly path?: string;
}
